import { datatype, random } from 'faker'
import { FormikValues } from 'formik'

import { momentDateHandler } from '../../date/moment-date-handler'
import {
  getInstrumentCodes,
  InstrumentCode,
  InterestPaid,
  LongRatings,
  MaturityAction,
  ShortRatings,
} from '../../models'

const { formatIso, newDate, addMonths } = momentDateHandler

interface InstrumentDetails {
  id: number
  isin: string
  frnIssueMargin?: number
  cleanPrice: number
  grossPrice: number
  quantity: number
  sellingCleanPrice?: number
  sellingGrossPrice?: number
  sellingQuantity?: number
  sellingDate?: Date
  createdAt: Date
  updatedAt: Date
  apir?: string
  price?: number
  operation?: string
}

interface TradeHistory {
  href: string
  transactionDate: Date
  quantity: number
  cleanPrice: number
  grossPrice: number
  sellingCleanPrice?: number
  sellingGrossPrice?: number
  quantitySold?: number
  capitalValue?: number
  grossValue?: number
  sellingDate?: Date
}

interface Interest {
  accrued: number
  monthToDate: number
  expected?: null
  market: number
}

interface Interests {
  current: Interest
}

interface Issuer {
  name: string
  code: string
  longRating: string
  shortRating: string
}

interface IsinPrice {
  yieldOrTradeMargin: null
  priceDate: null
}

export interface Trade {
  id: number
  purchaseDate: Date
  maturityDate: Date
  coupon: number
  principal: number
  interestCompoundCode: InterestPaid
  interestPaidCode: InterestPaid
  instrumentCode: InstrumentCode
  allocationCode: string
  issuerId: number
  countryCode: string
  createdAt: Date
  updatedAt: Date
  note?: string
  reference?: string
  rootTradeId: number
  instrumentId: number
  transactionDate: Date
  maturityAction?: MaturityAction
  maturityActionConfirmed: boolean
  rfqId?: number
  sourceRfqUuid?: number
  bankReference?: string
  source?: string
  onBehalf: boolean
  ratesheetCoupon?: string
  sourceOfferId?: number
  safeCustodyCode?: string
  redeemedByUserId?: number
  redeemedAt?: Date
  issuerLeiCode?: string
  bankingGroupId: number
  fundsFossilFuels: boolean
  nextCouponDate?: Date
  notionalPrincipal: number
  instrumentDetails: InstrumentDetails
  rating: ShortRatings | LongRatings
  instrumentSpecificRating: ShortRatings | LongRatings
  history: TradeHistory[]
  portfolioPercentage: number
  interests: Interests
  issuer: Issuer
  isinPrice: IsinPrice
}

export interface ManagedFund extends Trade {
  price: number
  quantity: number
  apir: string
  operation: string
  isinCode: string
  operationDate?: Date | string
  operationPrice?: number
}

export const mockTrade = (overrides: Partial<Trade>): Partial<Trade> => ({
  id: datatype.number(),
  instrumentCode: random.arrayElement(getInstrumentCodes()),
  principal: datatype.float({ min: 1_000, max: 1_000_000_000_000, precision: 2 }),
  coupon: datatype.float({ min: 0, max: 100, precision: 2 }),
  issuerId: datatype.number(),
  allocationCode: datatype.string(1),
  purchaseDate: new Date(),
  maturityDate: addMonths({ months: 12, date: newDate() }).toDate(),
  ...overrides,
})

export interface UpdateTradeFormValues extends FormikValues {
  principal: number
  notionalPrincipal?: number
  coupon: number
  allocationCode: string
  purchaseDate: Date | string
  maturityDate: Date | string
  interestPaidCode: InterestPaid
  note?: string
  reference?: string
  bankReference?: string
}

export const updateTradeFormInitialValues: UpdateTradeFormValues = {
  principal: 0,
  notionalPrincipal: null,
  coupon: 0,
  allocationCode: '',
  purchaseDate: null,
  maturityDate: null,
  interestPaidCode: null,
  note: null,
  reference: null,
  bankReference: null,
}

export interface UpdateManagedFundFormValues extends FormikValues {
  quantityToKeep: number
  price: number
  transactionDate: Date | string
  note?: string
  reference?: string
  bankReference?: string
}

export const updateManagedFundFormInitialValues: UpdateManagedFundFormValues = {
  quantityToKeep: null,
  price: 0,
  transactionDate: null,
  note: null,
  reference: null,
  bankReference: null,
}
export interface UpdateCouponDatesFormValues {
  couponDates: { couponDate: Date }[]
}

export interface TradeTableItem {
  id: number
  instrumentCode: InstrumentCode
  issuerCode: string
  principal: number
  accruedInterest: number
  interestPaidCode: InterestPaid
  allocationCode: string
  purchaseDate: Date
  maturityDate: Date
  nextCouponDate?: Date
}

export const convertTradeToTableItems = (trades: Trade[]): TradeTableItem[] =>
  trades?.map((trade) => ({
    ...trade,
    issuerName: trade.issuer.name,
    issuerCode: trade.issuer.code,
    accruedInterest: trade.interests.current.accrued,
  })) || []

export const convertTradeUpdateFormValuesToUpdateTradePayload = (values: UpdateTradeFormValues) => {
  const updateTradePayload = { ...values }

  if (updateTradePayload.purchaseDate) {
    updateTradePayload.purchaseDate = formatIso({ date: newDate(updateTradePayload.purchaseDate as Date) })
  }
  if (updateTradePayload.maturityDate) {
    updateTradePayload.maturityDate = formatIso({ date: newDate(updateTradePayload.maturityDate as Date) })
  }

  if (updateTradePayload.notionalPrincipal) {
    updateTradePayload.principal = updateTradePayload.notionalPrincipal
  }

  return updateTradePayload
}

export const convertCouponDatesFormValuesToCouponDatesData = (values: UpdateCouponDatesFormValues) =>
  values.couponDates.map(({ couponDate }) => formatIso({ date: newDate(couponDate) }))
